/**
 * sl-4rt9: Local-workspace support classification for workspace settings tabs.
 *
 * This module is the Single Source of Truth for the set of registered
 * workspace sidebar tabs. The sidebar registry in index.tsx imports
 * RegisteredWorkspaceTab and types its `key` field against it, so adding
 * a new tab to index.tsx without updating WORKSPACE_TAB_LOCAL_SUPPORT
 * fails tsc at compile time (primary contract enforcement). The spec
 * `__tests__/local-unsupported-tabs.spec.ts` adds an AST-level check
 * over index.tsx that forbids every form of indirection capable of
 * laundering an unclassified tab past the compile-time contract
 * (type assertions, non-literal keys, spread elements).
 *
 * DO NOT use `as RegisteredWorkspaceTab` — or any other form of
 * indirection — to silence a type error on a new registry entry. The
 * cast turns an unclassified literal into a "valid" entry at compile
 * time while leaving the SSoT (and therefore the local-workspace filter,
 * URL-direct-nav guard, and render switch) unaware of it. The correct
 * fix for a "type '…' is not assignable to RegisteredWorkspaceTab" error
 * is to add the new tab to WORKSPACE_TAB_LOCAL_SUPPORT below with the
 * correct classification.
 *
 * Classifications:
 *   - 'blocked'       — Tab fires workspace-scoped cloud queries without a
 *                       per-panel local guard. URL-direct navigation would
 *                       403 from WorkspacePermissionGuard. Filtered at the
 *                       router layer via LOCAL_UNSUPPORTED_TABS.
 *   - 'panel-guarded' — Tab has its own local-flavour early-return or a
 *                       content-switch, so it renders safely for local
 *                       workspaces.
 *   - 'local-safe'    — Tab uses no workspace-scoped cloud queries.
 */
export type LocalWorkspaceTabSupport =
  | 'blocked'
  | 'panel-guarded'
  | 'local-safe';

export const WORKSPACE_TAB_LOCAL_SUPPORT = {
  'workspace:preference': 'panel-guarded',
  'workspace:properties': 'local-safe',
  'workspace:roles': 'blocked',
  'workspace:permission-graph': 'blocked',
  'workspace:groups': 'panel-guarded',
  'workspace:members': 'panel-guarded',
  'workspace:guests': 'panel-guarded',
  'workspace:permissions': 'panel-guarded',
  'workspace:folders': 'panel-guarded',
  'workspace:team-areas': 'panel-guarded',
  'workspace:credentials': 'blocked',
  'workspace:webhooks': 'blocked',
  'workspace:workflows': 'blocked',
  'workspace:saml': 'blocked',
  'workspace:audit-trail': 'blocked',
  'workspace:mcp-access': 'blocked',
  'workspace:security': 'panel-guarded',
  'workspace:ai': 'blocked',
  'workspace:automations': 'blocked',
  'workspace:integrations': 'panel-guarded',
  'workspace:storage': 'panel-guarded',
  'workspace:plans': 'blocked',
  'workspace:billing': 'blocked',
  'workspace:custom-apps': 'blocked',
  'workspace:mail': 'blocked',
  'workspace:pdf-signatures': 'blocked',
} as const satisfies Record<string, LocalWorkspaceTabSupport>;

export type RegisteredWorkspaceTab = keyof typeof WORKSPACE_TAB_LOCAL_SUPPORT;

export const REGISTERED_WORKSPACE_TAB_KEYS = Object.keys(
  WORKSPACE_TAB_LOCAL_SUPPORT
) as RegisteredWorkspaceTab[];

export const LOCAL_UNSUPPORTED_TABS: ReadonlySet<string> = new Set(
  (
    Object.entries(WORKSPACE_TAB_LOCAL_SUPPORT) as Array<
      [string, LocalWorkspaceTabSupport]
    >
  )
    .filter(([, v]) => v === 'blocked')
    .map(([k]) => k)
);
